<template>
  <ehc-page>
    <v-card class="pa-4">
      <ehc-header text="Task Manager"></ehc-header>

      <ehc-sheet>
        <!-- Add New Task -->
        <div class="mb-4">
          <v-text-field
            v-model="newTask"
            label="Add a new task"
            outlined
            dense
            @keyup.enter="addTask"
            append-icon="mdi-plus"
            @click:append="addTask"
          ></v-text-field>
        </div>

        <!-- Tasks List -->
        <div v-if="tasks.length === 0" class="text-center pa-4 text-disabled">
          No tasks yet. Add one to get started!
        </div>

        <v-list v-else>
          <v-list-item v-for="task in tasks" :key="task.id" class="task-item">
            <v-list-item-content>
              <v-list-item-title
                :class="{ 'text-decoration-line-through': task.completed }"
              >
                {{ task.title }}
              </v-list-item-title>
              <v-list-item-subtitle v-if="task.createdAt">
                Created: {{ formatDate(task.createdAt) }}
              </v-list-item-subtitle>
            </v-list-item-content>

            <v-list-item-action>
              <v-checkbox
                :input-value="task.completed"
                @change="toggleTask(task.id, $event)"
              ></v-checkbox>
            </v-list-item-action>

            <v-list-item-action>
              <v-btn icon small @click="deleteTask(task.id)">
                <v-icon small>mdi-delete</v-icon>
              </v-btn>
            </v-list-item-action>
          </v-list-item>
        </v-list>

        <!-- Task Statistics -->
        <v-divider class="my-4"></v-divider>
        <div class="text-center text-sm text-disabled">
          {{ completedCount }} of {{ tasks.length }} tasks completed
        </div>
      </ehc-sheet>
    </v-card>
  </ehc-page>
</template>

<script>
import firebase from "firebase";
import apiMixin from "@/mixins/api.vue";

export default {
  name: "TaskManager",
  mixins: [apiMixin],
  data() {
    return {
      newTask: "",
      tasks: [],
      loading: false,
      db: firebase.firestore(),
    };
  },
  computed: {
    completedCount() {
      return this.tasks.filter((t) => t.completed).length;
    },
  },
  mounted() {
    this.loadTasks();
  },
  methods: {
    async loadTasks() {
      try {
        this.loading = true;
        const userId = this.$store.getters.user?.uid;
        if (!userId) return;

        const snapshot = await this.db
          .collection("tasks")
          .where("userId", "==", userId)
          .get();

        this.tasks = snapshot.docs
          .map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }))
          .sort((a, b) => {
            const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt) || 0;
            const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt) || 0;
            return dateB - dateA;
          });
      } catch (error) {
        console.error("Error loading tasks:", error);
        this.showAlert("Error loading tasks");
      } finally {
        this.loading = false;
      }
    },

    async addTask() {
      if (!this.newTask.trim()) return;

      try {
        const userId = this.$store.getters.user?.uid;
        if (!userId) {
          this.showAlert("You must be logged in");
          return;
        }

        await this.db.collection("tasks").add({
          title: this.newTask.trim(),
          completed: false,
          userId,
          createdAt: new Date(),
          updatedAt: new Date(),
        });

        this.newTask = "";
        this.loadTasks();
        this.showAlert("Task added successfully");
      } catch (error) {
        console.error("Error adding task:", error);
        this.showAlert("Error adding task");
      }
    },

    async toggleTask(taskId, completed) {
      try {
        await this.db.collection("tasks").doc(taskId).update({
          completed,
          updatedAt: new Date(),
        });
        this.loadTasks();
      } catch (error) {
        console.error("Error updating task:", error);
        this.showAlert("Error updating task");
      }
    },

    async deleteTask(taskId) {
      try {
        await this.db.collection("tasks").doc(taskId).delete();
        this.loadTasks();
        this.showAlert("Task deleted");
      } catch (error) {
        console.error("Error deleting task:", error);
        this.showAlert("Error deleting task");
      }
    },

    formatDate(date) {
      if (!date) return "";
      const d = date.toDate ? date.toDate() : new Date(date);
      return d.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    showAlert(message) {
      this.$store.commit("setAlert", {
        show: true,
        message,
      });
      setTimeout(() => {
        this.$store.commit("setShowAlert", false);
      }, 1000);
    },
  },
};
</script>

<style scoped>
.task-item {
  border-bottom: 1px solid #eee;
}

.task-item:hover {
  background-color: #f5f5f5;
}
</style>
